/**
 * Admin page: Homepage Layout — reorder & toggle visibility of storefront
 * homepage sections. Saves into the `homepage_layout_order` WebsiteSetting.
 *
 * UX: vertical list of section cards with:
 *  - Drag handle (HTML5 native drag-drop — no extra deps)
 *  - Visible toggle
 *  - Section label + description
 *  - "Reset to default" button to clear admin overrides
 *  - "Save layout" button to persist
 *
 * Source of truth for section ids + defaults: @terra/contracts/cms
 * (shared between storefront and admin panels).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowDown, ArrowUp, GripVertical, RotateCcw, Save } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

import { cms } from '@terra/contracts';
import { Button, Spinner } from '@terra/ui';

import { LivePreviewPanel } from '../components/LivePreviewPanel';
import { api } from '../lib/api';
import { usePreviewStore } from '../stores/previewStore';

type HomepageLayoutItem = cms.HomepageLayoutItem;
type HomepageLayoutSetting = cms.HomepageLayoutSetting;
type HomepageSectionId = cms.HomepageSectionId;

const { DEFAULT_HOMEPAGE_LAYOUT, HOMEPAGE_SECTION_CATALOG, resolveHomepageLayout } = cms;

const catalogById = new Map(HOMEPAGE_SECTION_CATALOG.map((s) => [s.id, s]));

export function HomepageLayoutPage() {
  const qc = useQueryClient();

  const { data: setting, isLoading } = useQuery({
    queryKey: ['website-setting', 'homepage_layout_order'],
    queryFn: () => api.cms.getSetting('homepage_layout_order').catch(() => null),
  });

  const [items, setItems] = useState<HomepageLayoutItem[]>(DEFAULT_HOMEPAGE_LAYOUT.items);
  const [dirty, setDirty] = useState(false);
  // dragIndex tracks the row currently being dragged (HTML5 drag native)
  const [dragIndex, setDragIndex] = useState<number | null>(null);

  // Hydrate local state once the setting loads.
  useEffect(() => {
    if (setting !== undefined) {
      const resolved = resolveHomepageLayout(
        (setting?.value as HomepageLayoutSetting | null) ?? null,
      );
      setItems(resolved);
      setDirty(false);
    }
  }, [setting]);

  const upsert = useMutation({
    mutationFn: () =>
      api.cms.upsertSetting('homepage_layout_order', { items } satisfies HomepageLayoutSetting),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['website-setting', 'homepage_layout_order'] });
      void qc.invalidateQueries({
        queryKey: ['website-public-setting', 'homepage_layout_order'],
      });
      setDirty(false);
      // Bump LivePreviewPanel so the right-side iframe reloads instantly.
      usePreviewStore.getState().bump();
    },
  });

  const visibleCount = useMemo(() => items.filter((i) => i.visible).length, [items]);

  function move(from: number, to: number) {
    if (to < 0 || to >= items.length || from === to) return;
    const next = items.slice();
    const [removed] = next.splice(from, 1);
    if (!removed) return;
    next.splice(to, 0, removed);
    setItems(next);
    setDirty(true);
  }

  function toggleVisible(id: HomepageSectionId) {
    setItems((prev) => prev.map((it) => (it.id === id ? { ...it, visible: !it.visible } : it)));
    setDirty(true);
  }

  function resetToDefault() {
    setItems(DEFAULT_HOMEPAGE_LAYOUT.items.slice());
    setDirty(true);
  }

  if (isLoading) {
    return (
      <div className="grid min-h-[300px] place-items-center">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="grid gap-6 pb-8 xl:grid-cols-[1fr_minmax(380px,460px)]">
      {/* LEFT — editor */}
      <div className="space-y-6">
        {/* Header */}
        <div className="mb-2 flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
          <div>
            <h1 className="text-ink text-[22px] font-extrabold tracking-tight">Homepage Layout</h1>
            <p className="text-ink-muted mt-1 text-[13px] font-medium">
              Drag to reorder. Toggle visibility per section. Hidden sections won&apos;t render on
              the storefront homepage.
            </p>
          </div>

          <div className="flex items-center gap-2">
            <Button variant="ghost" onClick={resetToDefault}>
              <RotateCcw className="mr-1.5 h-4 w-4" /> Reset to default
            </Button>
            <Button loading={upsert.isPending} disabled={!dirty} onClick={() => upsert.mutate()}>
              <Save className="mr-1.5 h-4 w-4" /> Save layout
            </Button>
          </div>
        </div>

        <div className="border-border/60 bg-surface rounded-2xl border shadow-sm">
          <div className="border-border/60 border-b px-5 py-4">
            <h2 className="text-ink text-[15px] font-bold">
              Storefront homepage sections
              <span className="text-ink-muted ml-2 text-[12px] font-medium">
                {visibleCount} of {items.length} visible
              </span>
            </h2>
          </div>
          <div className="p-5">
            <ul className="space-y-2">
              {items.map((item, idx) => {
                const meta = catalogById.get(item.id);
                if (!meta) return null;
                const isDragging = dragIndex === idx;

                return (
                  <li
                    key={item.id}
                    draggable
                    onDragStart={(e) => {
                      setDragIndex(idx);
                      e.dataTransfer.effectAllowed = 'move';
                      // Hint Firefox that drag was initiated
                      try {
                        e.dataTransfer.setData('text/plain', String(idx));
                      } catch {
                        /* some browsers throw; safe to ignore */
                      }
                    }}
                    onDragOver={(e) => {
                      e.preventDefault();
                      e.dataTransfer.dropEffect = 'move';
                    }}
                    onDrop={(e) => {
                      e.preventDefault();
                      if (dragIndex !== null && dragIndex !== idx) {
                        move(dragIndex, idx);
                      }
                      setDragIndex(null);
                    }}
                    onDragEnd={() => setDragIndex(null)}
                    className={`border-border/60 bg-surface flex items-center gap-3 rounded-xl border p-3 transition-all ${
                      isDragging ? 'opacity-50' : 'hover:shadow-sm'
                    } ${!item.visible ? 'opacity-60' : ''}`}
                  >
                    {/* Drag handle */}
                    <div
                      className="text-ink-muted hover:text-ink shrink-0 cursor-grab active:cursor-grabbing"
                      aria-label="Drag to reorder"
                    >
                      <GripVertical className="h-5 w-5" />
                    </div>

                    {/* Up / down buttons — keyboard / non-drag fallback */}
                    <div className="flex flex-col gap-0.5">
                      <button
                        type="button"
                        onClick={() => move(idx, idx - 1)}
                        disabled={idx === 0}
                        className="text-ink-muted hover:bg-surface-subtle hover:text-ink grid h-5 w-5 place-items-center rounded transition-colors disabled:opacity-30"
                        aria-label="Move up"
                      >
                        <ArrowUp className="h-3.5 w-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => move(idx, idx + 1)}
                        disabled={idx === items.length - 1}
                        className="text-ink-muted hover:bg-surface-subtle hover:text-ink grid h-5 w-5 place-items-center rounded transition-colors disabled:opacity-30"
                        aria-label="Move down"
                      >
                        <ArrowDown className="h-3.5 w-3.5" />
                      </button>
                    </div>

                    {/* Position badge */}
                    <div className="bg-surface-subtle border-border/60 text-ink-muted grid h-7 w-7 shrink-0 place-items-center rounded-lg border text-[12px] font-bold">
                      {idx + 1}
                    </div>

                    {/* Title + description */}
                    <div className="min-w-0 flex-1">
                      <p className="text-ink text-[13px] font-bold">{meta.label}</p>
                      <p className="text-ink-muted truncate text-[12px] font-medium">
                        {meta.description}
                      </p>
                    </div>

                    {/* Visible toggle */}
                    <button
                      type="button"
                      onClick={() => toggleVisible(item.id)}
                      className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${
                        item.visible
                          ? 'bg-brand-primary'
                          : 'bg-surface-subtle border-border/60 border'
                      }`}
                      aria-label={item.visible ? 'Hide section' : 'Show section'}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform ${
                          item.visible ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>

        {/* Sticky footer save bar — handy on long lists */}
        {dirty && (
          <div className="bg-surface border-border/60 sticky bottom-4 flex items-center justify-between rounded-xl border p-3 shadow-lg">
            <span className="text-ink-muted text-[12px] font-medium">
              You have unsaved layout changes.
            </span>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                onClick={() => {
                  const resolved = resolveHomepageLayout(
                    (setting?.value as HomepageLayoutSetting | null) ?? null,
                  );
                  setItems(resolved);
                  setDirty(false);
                }}
              >
                Discard
              </Button>
              <Button loading={upsert.isPending} onClick={() => upsert.mutate()}>
                <Save className="mr-1.5 h-4 w-4" /> Save layout
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* RIGHT — live storefront iframe; auto-reloads when upsert.onSuccess
          bumps usePreviewStore. Hidden on smaller screens to keep the editor
          usable; admin can still preview by opening in a new tab. */}
      <div className="hidden xl:block">
        <LivePreviewPanel />
      </div>
    </div>
  );
}
